import { supabase } from './supabase';
import type { Invoice } from '../types/invoice';
import type { B2BBuyer } from '../types/b2b';

interface CartItem {
  name: string;
  sku?: string;
  color_name?: string;
  size_name?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product_id?: string;
  // Pre-discount catalog price captured at creation so the admin invoice editor
  // can re-divide by a new divisor without drifting to a changed catalog price.
  base_unit_price?: number;
}

interface CreateProFormaResult {
  success: boolean;
  invoice?: Invoice;
  error?: string;
}

interface InsertProFormaOptions {
  adminId: string;
  buyer: B2BBuyer;
  items: CartItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  vatPercentage?: number;
  reservationId?: string | null;
  invoiceDate?: Date;
}

export interface CreateManualProFormaOptions {
  adminId: string;
  buyer: B2BBuyer;
  items: CartItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  vatPercentage?: number;
  invoiceDate?: Date;
}

async function generateNumericInvoiceNumber(adminId: string, year?: number): Promise<string> {
  const invoiceYear = year ?? new Date().getFullYear();
  const yearStart = `${invoiceYear}-01-01`;
  const yearEnd = `${invoiceYear + 1}-01-01`;

  const { count } = await supabase
    .from('invoices')
    .select('id', { count: 'exact', head: true })
    .eq('admin_id', adminId)
    .gte('invoice_date', yearStart)
    .lt('invoice_date', yearEnd);

  const next = (count || 0) + 1;
  return `${invoiceYear}${String(next).padStart(5, '0')}`;
}

function buildItemDescription(item: CartItem): string {
  const parts: string[] = [];
  if (item.name) parts.push(item.name);
  if (item.sku) parts.push(`(${item.sku})`);
  const variant = [item.color_name, item.size_name].filter(Boolean).join(' / ');
  let base = parts.join(' ').trim();
  if (variant) base = base ? `${base} — ${variant}` : variant;
  return base || 'Item';
}

async function insertProForma(opts: InsertProFormaOptions): Promise<CreateProFormaResult> {
  const {
    adminId,
    buyer,
    items,
    subtotal,
    taxAmount,
    totalAmount,
    vatPercentage,
    reservationId = null,
    invoiceDate,
  } = opts;

  try {
    const effectiveVatPct = vatPercentage ?? buyer.vat_percentage ?? 21;
    const invoiceMoment = invoiceDate ?? new Date();
    const invoiceYear = invoiceMoment.getFullYear();

    let invoiceNumber: string;
    try {
      invoiceNumber = await generateNumericInvoiceNumber(adminId, invoiceYear);
    } catch {
      invoiceNumber = String(Date.now());
    }

    console.log('[proforma] generated invoice_number =', invoiceNumber);

    const dueDate = new Date(invoiceMoment);
    dueDate.setDate(dueDate.getDate() + 14);

    const customerAddress = [
      buyer.billing_address,
      `${buyer.billing_postal_code || ''} ${buyer.billing_city || ''}`.trim(),
      buyer.billing_country && buyer.billing_country !== 'NL' ? buyer.billing_country : null,
    ].filter(Boolean).join('\n');

    const invoicePayload: Record<string, unknown> = {
      admin_id: adminId,
      created_by: adminId,
      invoice_number: invoiceNumber,
      invoice_date: invoiceMoment.toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      customer_name: buyer.trade_name,
      customer_address: customerAddress || null,
      customer_vat_number: buyer.vat_number || null,
      customer_email: buyer.email,
      subtotal_amount: subtotal,
      tax_amount: taxAmount,
      total_amount: totalAmount,
      payment_method: 'bank_transfer',
      payment_status: 'unpaid',
      language: buyer.preferred_language || 'en',
      is_pro_forma: true,
      buyer_id: buyer.id,
    };

    if (reservationId) {
      invoicePayload.reservation_id = reservationId;
    }

    let { data: invoice, error: insertError } = await supabase
      .from('invoices')
      .insert(invoicePayload)
      .select()
      .single();

    if (insertError && (insertError.code === 'PGRST204' || insertError.code === '42703')) {
      const { buyer_id: _buyerId, reservation_id: _resId, ...fallbackPayload } = invoicePayload;
      const retry = await supabase
        .from('invoices')
        .insert(fallbackPayload)
        .select()
        .single();
      invoice = retry.data as typeof invoice;
      insertError = retry.error as typeof insertError;
    }

    if (insertError) throw insertError;
    if (!invoice) throw new Error('No invoice returned');

    const invoiceItems = items.map(item => ({
      invoice_id: invoice.id,
      description: buildItemDescription(item),
      product_name: item.name || null,
      sku: item.sku || null,
      color_name: item.color_name || null,
      size_name: item.size_name || null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      total_price: item.total_price,
      base_unit_price: item.base_unit_price ?? null,
      product_id: item.product_id || null,
      vat_rate: effectiveVatPct,
      vat_amount: parseFloat((item.total_price * (effectiveVatPct / 100)).toFixed(2)),
    }));

    let { error: itemsError } = await supabase
      .from('invoice_items')
      .insert(invoiceItems);

    if (itemsError && (itemsError.code === 'PGRST204' || itemsError.code === '42703')) {
      const fallbackItems = invoiceItems.map(({ product_name: _pn, sku: _sk, color_name: _cn, size_name: _sz, base_unit_price: _bup, ...rest }) => rest);
      const retry = await supabase.from('invoice_items').insert(fallbackItems);
      itemsError = retry.error as typeof itemsError;
    }

    if (itemsError) {
      console.error('Failed to insert invoice items:', itemsError);
      await supabase
        .from('invoices')
        .delete()
        .eq('id', invoice.id);
      throw itemsError;
    }

    const formattedInvoice: Invoice = {
      id: invoice.id,
      user_id: invoice.admin_id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      due_date: invoice.due_date,
      customer_name: invoice.customer_name,
      customer_address: invoice.customer_address,
      customer_vat_number: invoice.customer_vat_number,
      customer_email: invoice.customer_email,
      subtotal_amount: parseFloat(invoice.subtotal_amount),
      tax_amount: parseFloat(invoice.tax_amount),
      total_amount: parseFloat(invoice.total_amount),
      payment_method: invoice.payment_method,
      payment_status: invoice.payment_status,
      language: invoice.language || 'en',
      currency: 'EUR',
      is_pro_forma: true,
      reservation_id: reservationId ?? undefined,
      buyer_id: buyer.id,
      created_at: invoice.created_at,
      updated_at: invoice.updated_at,
      invoice_items: invoiceItems.map((item, idx) => ({
        id: `temp-${idx}`,
        invoice_id: invoice.id,
        description: item.description,
        product_name: item.product_name,
        sku: item.sku,
        color_name: item.color_name,
        size_name: item.size_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        vat_rate: item.vat_rate,
        vat_amount: item.vat_amount,
        created_at: invoice.created_at,
      })) as Invoice['invoice_items'],
    };

    return { success: true, invoice: formattedInvoice };
  } catch (err: unknown) {
    console.error('Failed to create pro-forma invoice:', err);
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: message };
  }
}

export async function createManualProFormaInvoice(
  opts: CreateManualProFormaOptions
): Promise<CreateProFormaResult> {
  return insertProForma({
    ...opts,
    reservationId: null,
  });
}

export async function createProFormaInvoice(
  reservationId: string,
  reservationNumber: string,
  adminId: string,
  buyer: B2BBuyer,
  items: CartItem[],
  subtotal: number,
  taxAmount: number,
  totalAmount: number,
  vatPercentage?: number
): Promise<CreateProFormaResult> {
  void reservationNumber;
  return insertProForma({
    adminId,
    buyer,
    items,
    subtotal,
    taxAmount,
    totalAmount,
    vatPercentage,
    reservationId,
  });
}
